import { Link, NavLink, Outlet } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuthContext } from '../hooks/useAuth';

type Theme = 'light' | 'dark';

function readInitialTheme(): Theme {
  if (typeof document === 'undefined') return 'dark';
  const attr = document.documentElement.getAttribute('data-theme');
  return attr === 'light' ? 'light' : 'dark';
}

export function Shell() {
  const { profile, signOut } = useAuthContext();
  const [theme, setTheme] = useState<Theme>(readInitialTheme);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem('marketing-theme', theme);
    } catch {
      // Safari private mode — data-theme attribute alone is fine.
    }
  }, [theme]);

  const isDark = theme === 'dark';

  return (
    <div className="shell">
      <header className="shell-header">
        <Link to="/" className="shell-brand" aria-label="tripcode marketing — back to dashboard">
          <span className="brand-row">
            <span className="wordmark">tripcode<i className="caret" /></span>
            <span className="brand-suffix">marketing</span>
          </span>
        </Link>
        <nav style={{ display: 'flex', gap: 16, marginLeft: 24, fontSize: 14 }}>
          <ShellNavLink to="/campaigns">Campaigns</ShellNavLink>
          <ShellNavLink to="/characters">Characters</ShellNavLink>
        </nav>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 'auto' }}>
          {profile && (
            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              {profile.full_name || profile.email}
            </span>
          )}
          <button
            type="button"
            className="icon-btn"
            aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
            aria-pressed={!isDark}
            title={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
            onClick={() => setTheme(isDark ? 'light' : 'dark')}
          >
            <ThemeIcon dark={isDark} />
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => { void signOut(); }}
          >
            Sign out
          </button>
        </div>
      </header>
      <main className="shell-content">
        <Outlet />
      </main>
    </div>
  );
}

function ShellNavLink({ to, end, children }: { to: string; end?: boolean; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      end={end}
      style={({ isActive }) => ({
        color: isActive ? 'var(--color-text)' : 'var(--text-secondary)',
        fontWeight: isActive ? 600 : 500,
        textDecoration: 'none',
        padding: '4px 0',
        borderBottom: isActive ? '2px solid var(--color-primary, currentColor)' : '2px solid transparent',
      })}
    >
      {children}
    </NavLink>
  );
}

function ThemeIcon({ dark }: { dark: boolean }) {
  if (dark) {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
      </svg>
    );
  }
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}
